import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, apiError } from "@/lib/format";
import { PageHeader } from "@/components/Page";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value || "–"}</span>
    </div>
  );
}

const EMP_TYPE = { festangestellt: "Festangestellt", haushilfe: "Aushilfe" };

export default function Profil() {
  const { user, company, refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [contact, setContact] = useState({ email: "", phone: "", address: "", emergency_name: "", emergency_phone: "" });
  const [savingContact, setSavingContact] = useState(false);

  const openEdit = () => {
    setContact({ email: user?.email || "", phone: user?.phone || "", address: user?.address || "", emergency_name: user?.emergency_name || "", emergency_phone: user?.emergency_phone || "" });
    setEditOpen(true);
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.put("/auth/me/profile", contact);
      await refreshMe();
      toast.success("Angaben gespeichert");
      setEditOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingContact(false); }
  };

  const change = async () => {
    if (nw !== confirm) return toast.error("Die neuen Passwörter stimmen nicht überein");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Passwort geändert");
      setOpen(false);
      setCur(""); setNw(""); setConfirm("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Mein Profil"
        subtitle="Deine persönlichen Angaben."
        testid="profil-header"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openEdit} data-testid="edit-contact-open"><Pencil className="h-4 w-4 mr-2" /> Angaben bearbeiten</Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="change-password-open"><KeyRound className="h-4 w-4 mr-2" /> Passwort ändern</Button></DialogTrigger>
            <DialogContent data-testid="change-password-dialog">
              <DialogHeader><DialogTitle>Passwort ändern</DialogTitle><DialogDescription>Gib dein aktuelles und ein neues Passwort ein.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Aktuelles Passwort</Label><Input type="password" data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
                <div className="space-y-2"><Label>Neues Passwort</Label><Input type="password" data-testid="cp-new" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="mind. 4 Zeichen" /></div>
                <div className="space-y-2"><Label>Neues Passwort bestätigen</Label><Input type="password" data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
              </div>
              <DialogFooter><Button onClick={change} disabled={busy} data-testid="cp-submit" className="brand-bg text-white hover:opacity-90">Ändern</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="max-w-2xl grid gap-5">
        <Card className="p-6 border-slate-200 rounded-xl">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-14 w-14 rounded-full brand-bg flex items-center justify-center text-white text-xl font-bold">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900">{user?.name}</div>
              <div className="text-sm text-slate-500">{company?.name}</div>
            </div>
          </div>
          <Row label="Benutzername" value={user?.username} />
          <Row label="Anstellungsart" value={EMP_TYPE[user?.employee_type] || "–"} />
          <Row label="Wochenstunden" value={user?.weekly_hours ? `${user.weekly_hours} h` : "–"} />
          <Row label="Urlaubstage / Jahr" value={user?.vacation_days_total} />
          <Row label="E-Mail" value={user?.email} />
          <Row label="Telefon" value={user?.phone} />
          <Row label="Adresse" value={user?.address} />
          <Row label="Notfallkontakt" value={user?.emergency_name} />
          <Row label="Notfall-Telefon" value={user?.emergency_phone} />
          <Row label="Geburtsdatum" value={user?.birth_date ? fmtDate(user.birth_date) : "–"} />
          <Row label="Eintrittsdatum" value={user?.start_date ? fmtDate(user.start_date) : "–"} />
        </Card>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-contact-dialog">
          <DialogHeader><DialogTitle>Angaben bearbeiten</DialogTitle><DialogDescription>Ändere deine Kontaktdaten.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>E-Mail</Label><Input data-testid="contact-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="name@firma.de" /></div>
            <div className="space-y-2"><Label>Telefon</Label><Input data-testid="contact-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+49 …" /></div>
            <div className="space-y-2"><Label>Adresse</Label><Input data-testid="contact-address" value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} placeholder="Straße, PLZ Ort" /></div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <div className="space-y-2"><Label>Notfallkontakt</Label><Input data-testid="contact-emergency-name" value={contact.emergency_name} onChange={(e) => setContact({ ...contact, emergency_name: e.target.value })} placeholder="Name" /></div>
              <div className="space-y-2"><Label>Notfall-Telefon</Label><Input data-testid="contact-emergency-phone" value={contact.emergency_phone} onChange={(e) => setContact({ ...contact, emergency_phone: e.target.value })} placeholder="+49 …" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveContact} disabled={savingContact} data-testid="contact-submit" className="brand-bg text-white hover:opacity-90">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
